import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Circle, GoogleMap, OverlayView } from "@react-google-maps/api";

import MAP_STYLE from "./mapStyle";
import PriceMarker from "./PriceMarker";
import { boundsForRadius, regionKey } from "../../../lib/map/mapRegion";

const MAP_OPTIONS = {
  styles: MAP_STYLE,
  disableDefaultUI: true,
  zoomControl: false,
  clickableIcons: false,
  gestureHandling: "greedy",
  maxZoom: 18,
  minZoom: 3,
};

const CIRCLE_OPTIONS = {
  fillColor: "#AE6F28",
  fillOpacity: 0.14,
  strokeColor: "#AE6F28",
  strokeOpacity: 0.4,
  strokeWeight: 1,
  clickable: false,
};

const CONTAINER_STYLE = { width: "100%", height: "100%" };

const HomeMap = ({
  coordinates,
  radiusMeters,
  refitToken,
  markers,
  selectedId,
  onSelect,
  onOpen,
  fitPadding,
}) => {
  const mapRef = useRef(null);
  const appliedRef = useRef("");
  const pendingRef = useRef(null);
  const initialCenterRef = useRef(null);

  if (!initialCenterRef.current && coordinates) {
    initialCenterRef.current = {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    };
  }

  const initialCenter = initialCenterRef.current;

  const center = useMemo(
    () =>
      coordinates
        ? { lat: coordinates.latitude, lng: coordinates.longitude }
        : null,
    [coordinates]
  );

  const applyRegion = useCallback((map, target, meters, padding) => {
    const maps = window.google?.maps;
    if (!maps) return;

    const bounds = boundsForRadius({
      latitude: target.latitude,
      longitude: target.longitude,
      radiusMeters: meters,
    });

    map.fitBounds(
      new maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      ),
      padding
    );
  }, []);

  useEffect(() => {
    if (!coordinates) return;

    const key = regionKey(coordinates, radiusMeters, refitToken);
    if (appliedRef.current === key) return;

    if (!mapRef.current) {
      pendingRef.current = { coordinates, radiusMeters, key };
      return;
    }

    applyRegion(mapRef.current, coordinates, radiusMeters, fitPadding);
    appliedRef.current = key;
  }, [coordinates, radiusMeters, refitToken, fitPadding, applyRegion]);

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;

      const pending = pendingRef.current;
      if (!pending) return;

      applyRegion(map, pending.coordinates, pending.radiusMeters, fitPadding);
      appliedRef.current = pending.key;
      pendingRef.current = null;
    },
    [applyRegion, fitPadding]
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  if (!initialCenter || !center) {
    return <div className="home-map-placeholder" aria-hidden="true" />;
  }

  return (
    <GoogleMap
      center={initialCenter}
      zoom={12}
      options={MAP_OPTIONS}
      mapContainerStyle={CONTAINER_STYLE}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={() => onSelect(null)}
    >
      <Circle center={center} radius={radiusMeters} options={CIRCLE_OPTIONS} />

      <OverlayView
        position={center}
        mapPaneName={OverlayView.OVERLAY_LAYER}
        getPixelPositionOffset={(width, height) => ({
          x: -(width / 2),
          y: -(height / 2),
        })}
      >
        <span className="home-map-user-dot" />
      </OverlayView>
      {markers?.map((supplier) => (
        <PriceMarker
          key={supplier.id}
          supplier={supplier}
          selected={selectedId === supplier.id}
          onSelect={onSelect}
          onOpen={onOpen}
        />
      ))}
    </GoogleMap>
  );
};

export default React.memo(HomeMap);
